import StatTile from "../../common/StatTile";
import { AgencyTimeSaved, FeedItem, formatHours, TimeSaved, UsageResponse } from "./usageTypes";

function when(iso: string) {
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function HoursSavedCard({ timeSaved, typing }: { timeSaved: TimeSaved | null | undefined; typing: UsageResponse["typing"] }) {
  const personalized = typing?.personalized;
  const hours = timeSaved ? timeSaved.personalHours : 0;
  const def = timeSaved ? timeSaved.defaultHours : 0;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatTile
        label="Hours saved"
        value={formatHours(hours)}
        hint={personalized ? `at ${typing?.effectiveWpm} effective WPM` : "at the 30 WPM default"}
      />
      <StatTile label="At 30 WPM default" value={formatHours(def)} hint={timeSaved ? `${timeSaved.aiCases} AI-assisted reports` : "no AI-assisted reports"} />
      <StatTile
        label="Typing speed"
        value={typing?.medianWpm ? `${Math.round(typing.medianWpm)} WPM` : "Not measured"}
        hint={typing ? `median of recent bursts, ${typing.samples} samples` : "no editor typing recorded yet"}
      />
      <StatTile
        label="Used for hours saved"
        value={personalized ? "Personal" : "Default"}
        hint={personalized ? "own speed, scaled to report writing" : "needs 10 samples first"}
      />
    </div>
  );
}

export function AgencyHoursCard({ total }: { total: AgencyTimeSaved | null | undefined }) {
  const diff = total ? total.personalHours - total.defaultHours : 0;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatTile
        label="Hours saved"
        value={total ? formatHours(total.personalHours) : "..."}
        hint={total ? `${total.aiCases} AI-assisted reports` : "calculating"}
      />
      <StatTile label="At 30 WPM for everyone" value={total ? formatHours(total.defaultHours) : "..."} hint="the fixed model" />
      <StatTile
        label="Difference"
        value={total ? `${diff > 0 ? "+" : ""}${formatHours(diff)}` : "..."}
        hint="from personal typing speeds"
      />
      <StatTile
        label="Officers measured"
        value={total ? `${total.personalizedOfficers} of ${total.officers}` : "..."}
        hint="have enough typing samples"
      />
    </div>
  );
}

export function ActivityFeed({ feed, onCase }: { feed: FeedItem[]; onCase?: (id: string) => void }) {
  if (feed.length === 0) {
    return <p className="m-0 text-sm text-base-content/50">No audited actions in this range.</p>;
  }
  return (
    <ul className="m-0 p-0 list-none divide-y divide-base-200 rounded-xl border border-base-300 bg-base-100">
      {feed.map((f, i) => (
        <li key={`${f.at}-${i}`} className="flex items-baseline gap-3 px-3 py-2">
          <span className="w-32 shrink-0 text-xs text-base-content/50 tabular-nums">{when(f.at)}</span>
          <span className="text-sm font-medium text-base-content shrink-0">{f.label}</span>
          {f.detail && <span className="text-xs text-base-content/60 truncate">{f.detail}</span>}
          {f.caseId && onCase && (
            <button type="button" className="ml-auto text-xs text-blue-600 dark:text-blue-300 hover:underline shrink-0" onClick={() => onCase(f.caseId!)}>
              case
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
